import type { MargensFiltros } from '@triade/contracts';

/**
 * Filtros do painel de margens. Vivem na URL (`validateSearch`); tudo e apresentacao: o servidor filtra e agrega.
 */
export type TipoItem = NonNullable<MargensFiltros['tipo']>;
export type SituacaoMargens = NonNullable<MargensFiltros['status']>;

/** Origem do painel: `APP` e o padrao da API (orcamentos criados no app); `TODAS` inclui os importados do XARP. */
export type OrigemMargens = NonNullable<MargensFiltros['origem']>;

export interface BuscaMargens {
  dataDe?: string;
  dataAte?: string;
  tabelaId?: string;
  tipo?: TipoItem;
  /** Categoria do item (com as subcategorias) ou `SEM` = sem categoria; na URL e na consulta (mesmo nome). */
  categoriaId?: string;
  /** Na URL e `origemMargens` (`origem` ja e do filtro da lista de orcamentos); ausente = APP (padrao, nao vai na consulta). */
  origemMargens?: OrigemMargens;
  /** Na URL e `escopo` (`status` e `situacao` ja sao parametros de outras rotas, com outros valores); a API recebe `status`. */
  escopo?: SituacaoMargens;
  /** Na URL: so `APROVACAO` (periodo pela data da aprovacao). Ausente = do orcamento (padrao; nao vai na consulta). */
  dataRef?: 'APROVACAO';
}

export const TIPOS: TipoItem[] = ['PRODUTO', 'MERCADORIA', 'SERVICO', 'SERVICO_TERCEIRO'];
export const ORIGENS_MARGENS: OrigemMargens[] = ['APP', 'IMPORTADO_XARP', 'TODAS'];
export const ROTULO_ORIGEM: Record<OrigemMargens, string> = {
  APP: 'Criados no app (padrão)',
  IMPORTADO_XARP: 'Importados do XARP',
  TODAS: 'Todas as origens',
};
export const ROTULO_TIPO: Record<TipoItem, string> = { PRODUTO: 'Produto fabricado', MERCADORIA: 'Mercadoria', SERVICO: 'Serviço', SERVICO_TERCEIRO: 'Serviço de terceiro', MATERIA_PRIMA: 'Matéria-prima', MATERIAL_SECUNDARIO: 'Material secundário', EMBALAGEM: 'Embalagem', MATERIAL_AUXILIAR: 'Material auxiliar' };
export const ROTULO_SITUACAO: Record<SituacaoMargens, string> = { APROVADO: 'Aprovados', EM_ABERTO: 'Em aberto' };

const DATA = /^\d{4}-\d{2}-\d{2}$/;
const ID = /^[A-Za-z0-9_-]{1,64}$/;

/** `validateSearch` da rota: descarta o que nao for valido em vez de quebrar a tela. */
export function lerBusca(bruto: Record<string, unknown>): BuscaMargens {
  const dataDe = typeof bruto.dataDe === 'string' && DATA.test(bruto.dataDe) ? bruto.dataDe : undefined;
  const dataAte = typeof bruto.dataAte === 'string' && DATA.test(bruto.dataAte) ? bruto.dataAte : undefined;
  const tabelaId = typeof bruto.tabelaId === 'string' && ID.test(bruto.tabelaId) ? bruto.tabelaId : undefined;
  const tipo = TIPOS.find((t) => t === bruto.tipo);
  const categoriaId = typeof bruto.categoriaId === 'string' && ID.test(bruto.categoriaId) ? bruto.categoriaId : undefined;
  const escopo = bruto.escopo === 'APROVADO' || bruto.escopo === 'EM_ABERTO' ? bruto.escopo : undefined;
  const origemMargens = ORIGENS_MARGENS.find((o) => o === bruto.origemMargens && o !== 'APP');
  const dataRef = bruto.dataRef === 'APROVACAO' ? 'APROVACAO' : undefined;
  return {
    ...(origemMargens ? { origemMargens } : {}),
    ...(dataRef ? { dataRef } : {}),
    ...(dataDe ? { dataDe } : {}),
    ...(dataAte ? { dataAte } : {}),
    ...(tabelaId ? { tabelaId } : {}),
    ...(tipo ? { tipo } : {}),
    ...(categoriaId ? { categoriaId } : {}),
    ...(escopo ? { escopo } : {}),
  };
}

/** Parametros de GET /margens/* para a busca da URL (os mesmos nomes). */
export function paraConsulta(b: BuscaMargens): Record<string, string> {
  return {
    ...(b.dataDe ? { dataDe: b.dataDe } : {}),
    ...(b.dataAte ? { dataAte: b.dataAte } : {}),
    ...(b.tabelaId ? { tabelaId: b.tabelaId } : {}),
    ...(b.tipo ? { tipo: b.tipo } : {}),
    ...(b.categoriaId ? { categoriaId: b.categoriaId } : {}),
    ...(b.escopo ? { status: b.escopo } : {}),
    ...(b.origemMargens && b.origemMargens !== 'APP' ? { origem: b.origemMargens } : {}),
    ...(b.dataRef ? { dataRef: b.dataRef } : {}),
  };
}

export function temFiltro(b: BuscaMargens): boolean {
  return Boolean(b.dataDe || b.dataAte || b.tabelaId || b.tipo || b.categoriaId || b.escopo || b.dataRef || (b.origemMargens && b.origemMargens !== 'APP'));
}

/** 'AAAA-MM' como 'MM/AAAA' (reordena os campos). */
export function formatarMes(mes: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(mes);
  return m ? `${m[2]}/${m[1]}` : '—';
}

export type Faixa = 'green' | 'orange' | 'red';
/** Cores so dos tokens do design (globals.css). */
export const COR_FAIXA: Record<Faixa, string> = { green: 'bg-success', orange: 'bg-warning-dot', red: 'bg-destructive' };
